import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useForm,
  Controller,
  type Control,
  type UseFormSetValue,
  type UseFormGetValues,
  type UseFormWatch,
  type Resolver,
  type Path,
} from 'react-hook-form';
import {
  Tabs,
  Tab,
  Card,
  CardBody,
  Input,
  Textarea,
  Select,
  SelectItem,
  Switch,
  Chip,
  Spinner,
  Slider,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import {
  buildVoiceAgentDeltaPatchBody,
  sanitizeIdleTimeoutForApi,
  sanitizeKrispForApi,
  sanitizeFillerWordsForApi,
  sanitizeVadForApi,
} from './voiceAgentPayloadUtils';
import type { EditAgentFormValues } from './voiceAgentEditFormTypes';
import { VoiceAgentIdleTimeoutSection } from './VoiceAgentIdleTimeoutSection';
import { VoiceAgentKrispSection } from './VoiceAgentKrispSection';
import { VoiceAgentSummarizationSection } from './VoiceAgentSummarizationSection';
import { VoiceAgentVadSection } from './VoiceAgentVadSection';
import { VoiceAgentFillerWordsSection } from './VoiceAgentFillerWordsSection';
import PronunciationDictionarySection from './PronunciationDictionarySection';
import VoiceAgentBuiltInToolsSection from './VoiceAgentBuiltInToolsSection';
import {
  parseVoiceAgentBuiltInToolsState,
  areBuiltInToolsEqual,
  type VoiceAgentBuiltInToolsState,
  type WarmTransferToolFormState,
} from './voiceAgentToolsUtils';
import { validateVoiceAgentEditForm, type VoiceAgentEditValidationContext } from './voiceAgentFormValidation';
import {
  useGetAgentByIdQuery,
  useUpdateAgentMutation,
  useGetAllowedModelsQuery,
  useGetPhoneNumberListQuery,
  useGetInboundAttachListQuery,
  usePostInboundAttachMutation,
  useDeleteInboundAttachMutation,
  useGetAdvisorsQuery,
  useGetAgentAdvisorToolsQuery,
  usePostAdvisorToolMutation,
  usePostVoicePreviewMutation,
  formatPhoneForApi,
  getDefaultNumberFromResponse,
  getPhoneNumberOptionsFromList,
  normalizeFromNumberForApi,
  parseFromNumber,
  type AgentResponse,
  type PhoneNumberListResponse,
  type TemplateEntry,
  type AllowedModelsResponse,
  type AllowedModelsTtsEntry,
  parsePronunciationDictionaryConfig,
  normalizePronunciationDictionaryIds,
} from '@circuitry-ai/doc-data';
import { checkType, CtyButton, CtyShowToast, processMarkdownText, markdownPreviewStyles } from '@circuitry-ai/doc-ui';
import ReactMarkdown from 'react-markdown';
import { SystemPromptInlineSection } from './SystemPromptInlineSection';
import { VoiceAgentVariablesSection } from './VoiceAgentVariablesSection';
import { parseVariablesFromApi } from './voiceAgentVariablesUtils';

const PROMPT_PREVIEW_HEIGHT_PX = 240;
const promptPreviewStyles = `
  .voice-agent-prompt-textarea .nextui-textarea-wrapper,
  .voice-agent-prompt-textarea textarea {
    height: ${PROMPT_PREVIEW_HEIGHT_PX}px !important;
    min-height: ${PROMPT_PREVIEW_HEIGHT_PX}px !important;
    max-height: ${PROMPT_PREVIEW_HEIGHT_PX}px !important;
    resize: none !important;
    overflow-y: auto !important;
  }
  .voice-agent-prompt-preview pre,
  .voice-agent-prompt-preview code { white-space: pre-wrap; }
`;

const PHONE_PROVIDER_OPTIONS = [
  { value: 'twilio_US', provider: 'twilio', country_iso: 'US', label: '+1 (US • Twilio)' },
] as const;

/** Primary TTS emotions (Cartesia-compatible); values are lowercase for API. */
const TTS_EMOTION_OPTIONS = [
  { value: 'neutral', emoji: '😐' },
  { value: 'angry', emoji: '😠' },
  { value: 'excited', emoji: '🤩' },
  { value: 'content', emoji: '😊' },
  { value: 'sad', emoji: '😢' },
  { value: 'scared', emoji: '😨' },
] as const;

const TTS_EMOTION_BY_VALUE = Object.fromEntries(
  TTS_EMOTION_OPTIONS.map((o) => [o.value, o.emoji])
) as Record<string, string>;

function formatTtsEmotionLabel(emotion: string | undefined): string {
  if (!emotion) return '—';
  const emoji = TTS_EMOTION_BY_VALUE[emotion.toLowerCase()];
  const label = emotion.charAt(0).toUpperCase() + emotion.slice(1);
  return emoji ? `${emoji} ${label}` : label;
}

const TTS_VOLUME_MIN = 0.5;
const TTS_VOLUME_MAX = 2;
const TTS_VOLUME_DEFAULT = 1;
const TTS_SPEED_MIN = 0.6;
const TTS_SPEED_MAX = 1.5;
const TTS_SPEED_DEFAULT = 1;
const TTS_SLIDER_STEP = 0.05;

type DetailTabKey =
  | 'Basic Info'
  | 'Model'
  | 'Voice'
  | 'Transcriber'
  | 'Advance'
  | 'Phone Number'
  | 'Pronunciation'
  | 'Tools';

export interface VoiceAgentBasicInfoProps {
  agentName: string;
  workerId: string;
  description: string;
  onAgentNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  code?: string;
}

/** Matches agentcreation VoiceAgentFormValues (form shape for create voice agent). */
export interface VoiceAgentFormValues {
  name: string;
  pipeline_mode: string;
  from_number: string;
  phoneCode?: string;
  phoneNumber?: string;
  stt: { provider: string; model?: string; live_options: { model: string; language: string; smart_format: boolean; punctuate: boolean; numerals: boolean; profanity_filter: boolean; interim_results: boolean } };
  llm: { provider: string; model: string; temperature: number; system_prompt_template: string; system_prompt_template_outbound: string };
  first_message: { mode: string; text: string };
  first_message_outbound: { mode: string; text: string };
  report_only_initial_ttfb: boolean;
  idle_timeout: { enabled: boolean; timeout_seconds: number; retries: number; prompt_templates: string[] };
  filler_words: { enabled: boolean; filler_phrases: string[]; delay_seconds: number };
  summarization_enabled: boolean;
  webhook_urls: string[] | null;
  tools: { hangup_tool: { enabled: boolean }; business_tools: unknown[] };
  context_config: Record<string, unknown>;
  vad: Record<string, unknown>;
  tts: {
    provider: string;
    voice_id: string;
    default_language?: string;
    configs?: Record<string, TtsLanguageConfig>;
    emotion?: string;
    volume?: number;
    speed?: number;
  };
}
export interface TtsLanguageConfig {
  provider: string;
  voice_id: string;
  model_id?: string;
  model?: string;
  voice_settings?: Record<string, unknown>;
}

/** Get first key-value from allowed-models record (display key -> payload value). */
function recordToOption(r: Record<string, string> | undefined): { value: string; label: string } | null {
  if (!r || typeof r !== 'object') return null;
  const entries = Object.entries(r);
  if (entries.length === 0) return null;
  const [label, value] = entries[0];
  return { value: value ?? '', label: label ?? '' };
}

/** Build dropdown options from record: label = key (display), value = value (payload). */
function recordToOptions(r: Record<string, string> | undefined): Array<{ value: string; label: string }> {
  if (!r || typeof r !== 'object') return [];
  return Object.entries(r).map(([label, value]) => ({ label, value: value ?? '' }));
}

function formErrorMessage(e: { message?: string } | undefined): string | undefined {
  const m = e?.message;
  return typeof m === 'string' ? m : undefined;
}

function callDirectionFlags(detail: AgentResponse | undefined): {
  inboundEnabled: boolean;
  outboundEnabled: boolean;
} {
  if (!detail) {
    return { inboundEnabled: false, outboundEnabled: true };
  }
  const hasInbound = !!(detail as Record<string, unknown>).first_message;
  const hasOutbound = !!(detail as Record<string, unknown>).first_message_outbound;
  return {
    inboundEnabled: hasInbound,
    outboundEnabled: hasOutbound || !hasInbound,
  };
}

/** First-message opening (Model tab); one component for inbound, outbound, or side-by-side. */
const EditFirstOpeningTextarea = ({
  control,
  name,
  label,
  placeholder,
  firstMode,
  errorField,
}: {
  control: Control<EditAgentFormValues>;
  name: Path<EditAgentFormValues>;
  label: string;
  placeholder: string;
  firstMode: string | undefined;
  errorField: { message?: string } | undefined;
}) => <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <Textarea
        label={label}
        placeholder={placeholder}
        value={typeof field.value === 'string' ? field.value : ''}
        onValueChange={field.onChange}
        variant="bordered"
        minRows={2}
        isDisabled={firstMode === 'wait_for_user'}
        isRequired={firstMode === 'speak_first'}
        isInvalid={!!errorField}
        errorMessage={formErrorMessage(errorField)}
      />
    )}
  />

const EditFirstMessageModeSelect = ({
  control,
  name,
  label,
  errorField,
}: {
  control: Control<EditAgentFormValues>;
  name: Path<EditAgentFormValues>;
  label: string;
  errorField: { message?: string } | undefined;
}) => <Controller
    name={name}
    control={control}
    render={({ field }) => (
      <Select
        label={label}
        selectedKeys={typeof field.value === 'string' && field.value ? [field.value] : []}
        onSelectionChange={(keys) => {
          const k = Array.from(keys)[0] as string | undefined;
          field.onChange(k ?? '');
        }}
        variant="bordered"
        isInvalid={!!errorField}
        errorMessage={formErrorMessage(errorField)}
      >
        <SelectItem key="speak_first" value="speak_first">Assistant speaks first</SelectItem>
        <SelectItem key="wait_for_user" value="wait_for_user">Wait for user</SelectItem>
      </Select>
    )}
  />

/** Find TTS entry by provider value (e.g. "sarvam"). */
function findTtsEntryByProvider(entries: AllowedModelsTtsEntry[], providerValue: string): AllowedModelsTtsEntry | undefined {
  if (!providerValue) return undefined;
  return entries.find((e) => {
    const v = recordToOption(e.provider);
    return v && v.value === providerValue;
  });
}

function ttsScalarString(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = typeof v === 'string' ? v.trim() : String(v).trim();
  return s.length > 0 ? s : undefined;
}

function ttsNumericOrDefault(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function sliderChangeValue(value: number | number[]): number {
  return Array.isArray(value) ? (value[0] ?? 0) : value;
}

function normalizeTtsFormFromApi(ttsUnknown: unknown): EditAgentFormValues['tts'] | undefined {
  if (!ttsUnknown || typeof ttsUnknown !== 'object') return undefined;
  const r = ttsUnknown as Record<string, unknown>;
  const provider = typeof r.provider === 'string' && r.provider.trim() !== '' ? r.provider : undefined;
  if (!provider) return undefined;

  let voice_id: string | undefined = ttsScalarString(r.voice_id);
  if (!voice_id && (provider === 'openai' || provider === 'azure')) {
    voice_id = ttsScalarString(r.voice);
  }

  const fromModelId = ttsScalarString(r.model_id);
  const fromModel = ttsScalarString(r.model);
  let unifiedModel: string | undefined;
  if (provider !== 'azure') {
    if (provider === 'elevenlabs') unifiedModel = fromModelId ?? fromModel;
    else unifiedModel = fromModel ?? fromModelId;
  }

  const default_language = ttsScalarString(r.default_language);
  const emotion = ttsScalarString(r.emotion)?.toLowerCase();
  const volume = ttsNumericOrDefault(r.volume, TTS_VOLUME_DEFAULT);
  const speed = ttsNumericOrDefault(r.speed, TTS_SPEED_DEFAULT);

  return {
    provider,
    voice_id,
    model_id: unifiedModel,
    model: unifiedModel,
    default_language,
    emotion,
    volume,
    speed,
  };
}

function normalizeSttFormFromApi(sttUnknown: unknown): EditAgentFormValues['stt'] | undefined {
  if (!sttUnknown || typeof sttUnknown !== 'object') return undefined;
  const r = sttUnknown as Record<string, unknown>;
  const provider = typeof r.provider === 'string' && r.provider.trim() !== '' ? r.provider.trim() : undefined;
  if (!provider) return undefined;
  const loRaw = r.live_options;
  const lo = loRaw && typeof loRaw === 'object' ? (loRaw as Record<string, unknown>) : undefined;
  const fromLoModel = lo ? ttsScalarString(lo.model) : undefined;
  const fromLoLang = lo ? ttsScalarString(lo.language) : undefined;
  const unifiedModel = fromLoModel ?? ttsScalarString(r.model);
  const unifiedLanguage = fromLoLang ?? ttsScalarString(r.language);
  const live_options: { model?: string; language?: string } = {};
  if (unifiedModel) live_options.model = unifiedModel;
  if (unifiedLanguage) live_options.language = unifiedLanguage;
  return {
    provider,
    model: unifiedModel,
    ...(Object.keys(live_options).length ? { live_options } : {}),
  };
}

function formValuesFromAgent(data: AgentResponse | undefined): EditAgentFormValues {
  if (!data) {
    return { pronunciation: { enabled: false, dictionary_id: null } };
  }
  const llm = data.llm as EditAgentFormValues['llm'] | undefined;
  const firstMessage = data.first_message as EditAgentFormValues['first_message'] | undefined;
  const firstMessageOutbound = data.first_message_outbound as EditAgentFormValues['first_message_outbound'] | undefined;
  const fillerWords = data.filler_words as EditAgentFormValues['filler_words'] | undefined;
  const idleTimeout = sanitizeIdleTimeoutForApi({
    enabled: data.idle_timeout?.enabled ?? false,
    timeout_seconds: data.idle_timeout?.timeout_seconds,
    retries: data.idle_timeout?.retries,
    prompt_templates: data.idle_timeout?.prompt_templates,
  });
  const krisp = sanitizeKrispForApi({
    enabled: data.krisp?.enabled ?? false,
    noise_suppression_level: data.krisp?.noise_suppression_level,
  });
  const summarization = data.summarization as EditAgentFormValues['summarization'] | undefined;
  const fillerWordsNormalized = sanitizeFillerWordsForApi({
    enabled: fillerWords?.enabled ?? false,
    filler_phrases: fillerWords?.filler_phrases,
    delay_seconds: fillerWords?.delay_seconds,
    sllm_config: fillerWords?.sllm_config,
  });
  const vadRaw = data.vad as EditAgentFormValues['vad'] | undefined;
  const vad = sanitizeVadForApi({
    enabled: vadRaw?.enabled ?? false,
    confidence: vadRaw?.confidence,
    start_secs: vadRaw?.start_secs,
    stop_secs: vadRaw?.stop_secs,
    min_volume: vadRaw?.min_volume,
    preset: vadRaw?.preset,
    auto_adjust_for_turn_detection: vadRaw?.auto_adjust_for_turn_detection,
  });
  const { phoneCode, phoneNumber } = parseFromNumber(data.from_number ?? null);
  const pronunciationDictionary = parsePronunciationDictionaryConfig(
    data as Record<string, unknown>
  );
  return {
    llm: llm
      ? {
          provider: llm.provider,
          model: llm.model,
          temperature: llm.temperature,
          system_prompt_template: llm.system_prompt_template,
          system_prompt_template_outbound: llm.system_prompt_template_outbound,
        }
      : undefined,
    stt: normalizeSttFormFromApi(data.stt),
    tts: normalizeTtsFormFromApi(data.tts),
    first_message: firstMessage ? { mode: firstMessage.mode, text: firstMessage.text } : undefined,
    first_message_outbound: firstMessageOutbound
      ? { mode: firstMessageOutbound.mode, text: firstMessageOutbound.text }
      : undefined,
    from_number: data.from_number ?? null,
    phoneCode,
    phoneNumber,
    idle_timeout: idleTimeout,
    krisp,
    summarization_enabled: data.summarization_enabled ?? false,
    summarization: summarization
      ? {
          provider: summarization.provider,
          model: summarization.model,
          prompt_template: summarization.prompt_template,
        }
      : undefined,
    filler_words: fillerWordsNormalized,
    vad,
    pronunciation: pronunciationDictionary,
    variables: parseVariablesFromApi(data),
  };
}

function getLlmOptions(allowedModels: AllowedModelsResponse | undefined) {
  const llmEntries = allowedModels?.allowed_models?.llm ?? [];
  const providers = llmEntries
    .map((entry) => recordToOption(entry.provider))
    .filter((p): p is { value: string; label: string } => p != null);
  return { providers, llmEntries };
}
function getSttOptions(allowedModels: AllowedModelsResponse | undefined) {
  const sttEntries = allowedModels?.allowed_models?.stt ?? [];
  const providers = sttEntries
    .map((entry) => recordToOption(entry.provider))
    .filter((p): p is { value: string; label: string } => p != null);
  return { providers, sttEntries };
}

/** Update-mode tabs form (from VoiceAgentDetail; no Chat, Test Agent, or Delete). */
interface VoiceAgentDetailFormTabsProps {
  assistantId: string;
  basicInfo?: VoiceAgentBasicInfoProps;
  /** When true, hide the "Save changes" button; parent will trigger save via registerSaveHandler. */
  hideSaveButton?: boolean;
  /** When provided, register a function that runs voice save and returns Promise<boolean>. Parent calls it first, then update agent. */
  registerSaveHandler?: (runSave: () => Promise<boolean>) => void;
  /** When true, do not show toast on save (parent will show one success message). */
  silentSave?: boolean;
}

type SavedVoiceAgentFormSnapshot = {
  values: EditAgentFormValues;
  inboundEnabled: boolean;
  outboundEnabled: boolean;
  resolvedFromNumber: string | null;
  builtInTools: VoiceAgentBuiltInToolsState;
};

const preview_text = 'Hi, this is voice agent from circuitry ai, How are you doing?'

const VoiceAgentDetailFormTabs: React.FC<Readonly<VoiceAgentDetailFormTabsProps>> =
  ({ assistantId, basicInfo, hideSaveButton = false, registerSaveHandler, silentSave = false }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<DetailTabKey>(basicInfo ? 'Basic Info' : 'Model');
    const resolveRef = React.useRef<((value: boolean) => void) | null>(null);
    const savedSnapshotRef = useRef<SavedVoiceAgentFormSnapshot | null>(null);
    const [builtInToolsState, setBuiltInToolsState] = useState<VoiceAgentBuiltInToolsState>(() =>
      parseVoiceAgentBuiltInToolsState(undefined)
    );
    const [savedBuiltInTools, setSavedBuiltInTools] = useState<VoiceAgentBuiltInToolsState>(() =>
      parseVoiceAgentBuiltInToolsState(undefined)
    );

    const { data: agentDetail, isLoading: isLoadingAgent } = useGetAgentByIdQuery(assistantId, { skip: !assistantId });
    const { data: allowedModels } = useGetAllowedModelsQuery(undefined, { skip: !assistantId });
    const [updateAgent, { isLoading: isSaving }] = useUpdateAgentMutation();
    const [postInboundAttach, { isLoading: isSavingInbound }] = usePostInboundAttachMutation();
    const [deleteInboundAttach] = useDeleteInboundAttachMutation();

    const [greetingTab, setGreetingTab] = useState<'inbound' | 'outbound'>('inbound');

    const { inboundEnabled, outboundEnabled } = useMemo(
      () => callDirectionFlags(agentDetail),
      [agentDetail]
    );

    const editValidationContext = useMemo<VoiceAgentEditValidationContext>(
      () => ({ outboundEnabled, inboundEnabled, allowedModels }),
      [outboundEnabled, inboundEnabled, allowedModels]
    );

    const editResolver = useMemo<Resolver<EditAgentFormValues>>(
      () => async (values) => {
        const fieldErrors = validateVoiceAgentEditForm(values, editValidationContext, t);
        return {
          values,
          errors: Object.keys(fieldErrors).length > 0 ? fieldErrors : {},
        };
      },
      [editValidationContext, t]
    );

    const {
      control,
      handleSubmit,
      reset,
      watch,
      getValues,
      setValue,
      trigger,
      formState: { isDirty, errors, isValid },
    } = useForm<EditAgentFormValues>({
      defaultValues: formValuesFromAgent(agentDetail),
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: editResolver,
    });

    const buildSavedSnapshot = useCallback(
      (detail: AgentResponse | undefined): SavedVoiceAgentFormSnapshot | null => {
        if (!detail) return null;
        const values = formValuesFromAgent(detail);
        const { inboundEnabled: inbound, outboundEnabled: outbound } = callDirectionFlags(detail);
        const fromNumber =
          values.phoneCode && values.phoneNumber?.trim()
            ? formatPhoneForApi(values.phoneCode, values.phoneNumber.trim())
            : null;
        const resolvedFromNumber =
          fromNumber ??
          (!values.from_number?.trim() ? null : normalizeFromNumberForApi(values.from_number)) ??
          null;
        return {
          values,
          inboundEnabled: inbound,
          outboundEnabled: outbound,
          resolvedFromNumber,
          builtInTools: parseVoiceAgentBuiltInToolsState(detail as Record<string, unknown>),
        };
      },
      []
    );

    useEffect(() => {
      const snapshot = buildSavedSnapshot(agentDetail);
      savedSnapshotRef.current = snapshot;
      if (snapshot) {
        reset(snapshot.values);
        setBuiltInToolsState(snapshot.builtInTools);
        setSavedBuiltInTools(snapshot.builtInTools);
        trigger();
      }
    }, [agentDetail, buildSavedSnapshot, reset, trigger]);

    useEffect(() => {
      trigger();
    }, [editValidationContext, trigger]);

    const llmProvider = watch('llm.provider');
    const sttProvider = watch('stt.provider');
    const summarizationProvider = watch('summarization.provider');
    const inboundFirstMode = watch('first_message.mode');
    const outboundFirstMode = watch('first_message_outbound.mode');
    const phoneProviderCountry = watch('phoneCode');
    const llmOpts = useMemo(() => getLlmOptions(allowedModels), [allowedModels]);
    const sttOpts = useMemo(() => getSttOptions(allowedModels), [allowedModels]);
    const ttsEntriesFromAllowed = allowedModels?.allowed_models?.tts ?? [];
    const ttsProviderOptionsFromAllowed = useMemo(
      () =>
        ttsEntriesFromAllowed
          .map((e) => recordToOption(e.provider))
          .filter((o): o is { value: string; label: string } => o != null)
          .filter((o) => o.value !== 'dynamic'),
      [ttsEntriesFromAllowed]
    );
    const ttsProvider = watch('tts.provider');
    const selectedTtsEntry = useMemo(
      () => findTtsEntryByProvider(ttsEntriesFromAllowed, ttsProvider ?? ''),
      [ttsEntriesFromAllowed, ttsProvider]
    );
    const ttsVoiceOptionsFromAllowed = useMemo(
      () => recordToOptions(selectedTtsEntry?.voice_id ?? selectedTtsEntry?.voice),
      [selectedTtsEntry]
    );
    const ttsModelOptionsFromAllowed = useMemo(
      () => recordToOptions(selectedTtsEntry?.model_id ?? selectedTtsEntry?.model),
      [selectedTtsEntry]
    );
    const ttsLanguageOptionsFromAllowed = useMemo(
      () => recordToOptions(selectedTtsEntry?.language),
      [selectedTtsEntry]
    );
    const ttsModelValue = watch('tts.model_id') ?? watch('tts.model');
    const ttsVoiceIdValue = watch('tts.voice_id');
    const currentTtsProviderLabel = useMemo(
      () => ttsProviderOptionsFromAllowed.find((o) => o.value === ttsProvider)?.label ?? ttsProvider ?? '—',
      [ttsProviderOptionsFromAllowed, ttsProvider]
    );
    const currentTtsVoiceLabel = useMemo(
      () => ttsVoiceOptionsFromAllowed.find((o) => o.value === ttsVoiceIdValue)?.label ?? ttsVoiceIdValue ?? '—',
      [ttsVoiceOptionsFromAllowed, ttsVoiceIdValue]
    );
    const currentTtsModelLabel = useMemo(
      () => ttsModelOptionsFromAllowed.find((o) => o.value === ttsModelValue)?.label ?? ttsModelValue ?? '—',
      [ttsModelOptionsFromAllowed, ttsModelValue]
    );
    const llmModelsForProvider = useMemo(() => {
      if (!llmProvider) return [];
      const entry = llmOpts.llmEntries.find((e) => recordToOption(e.provider)?.value === llmProvider);
      return recordToOptions(entry?.model);
    }, [llmOpts.llmEntries, llmProvider]);
    const sttModelsForProvider = useMemo(() => {
      if (!sttProvider) return [];
      const entry = sttOpts.sttEntries.find((e) => recordToOption(e.provider)?.value === sttProvider);
      return recordToOptions(entry?.model);
    }, [sttOpts.sttEntries, sttProvider]);
    const summarizationModelsForProvider = useMemo(() => {
      if (!summarizationProvider) return [];
      const entry = sttOpts.sttEntries.find((e) => recordToOption(e.provider)?.value === summarizationProvider);
      return recordToOptions(entry?.model);
    }, [sttOpts.sttEntries, summarizationProvider]);
    const selectedPhoneOption = useMemo(() => PHONE_PROVIDER_OPTIONS.find((o) => o.value === phoneProviderCountry), [phoneProviderCountry]);
    const phoneProviderCountryInbound = watch('phoneCodeInbound');
    const selectedPhoneOptionInbound = useMemo(
      () => PHONE_PROVIDER_OPTIONS.find((o) => o.value === phoneProviderCountryInbound),
      [phoneProviderCountryInbound]
    );

    const { data: phoneListData } = useGetPhoneNumberListQuery(
      selectedPhoneOption ? { provider: selectedPhoneOption.provider, country_iso: selectedPhoneOption.country_iso } : undefined,
      { skip: !assistantId || !selectedPhoneOption }
    );
    const { data: phoneListDataInbound } = useGetPhoneNumberListQuery(
      selectedPhoneOptionInbound ? { provider: selectedPhoneOptionInbound.provider, country_iso: selectedPhoneOptionInbound.country_iso } : undefined,
      { skip: !assistantId || !selectedPhoneOptionInbound }
    );
    useEffect(() => {
      if (!assistantId || !selectedPhoneOption || !phoneListData) return;
      // Keep outbound dropdown selection from response (from_number) when it matches current provider
      if (agentDetail?.from_number) {
        const { phoneCode } = parseFromNumber(agentDetail.from_number);
        if (phoneCode === selectedPhoneOption?.value) return;
      }
      const raw = getDefaultNumberFromResponse(phoneListData);
      setValue('phoneNumber', parseFromNumber(raw).phoneNumber);
    }, [assistantId, selectedPhoneOption, phoneListData, setValue, agentDetail?.from_number]);
    useEffect(() => {
      if (!assistantId || !selectedPhoneOptionInbound || !phoneListDataInbound) return;
      const raw = getDefaultNumberFromResponse(phoneListDataInbound);
      setValue('phoneNumberInbound', parseFromNumber(raw).phoneNumber);
    }, [assistantId, selectedPhoneOptionInbound, phoneListDataInbound, setValue]);

    const { data: inboundAttachList, refetch: refetchInboundAttachList } = useGetInboundAttachListQuery(assistantId ?? '', { skip: !assistantId });
    const inboundAttached = useMemo(() => {
      const numbers = inboundAttachList?.numbers ?? [];
      return numbers.find((n) => n.assistant_id === assistantId);
    }, [inboundAttachList?.numbers, assistantId]);
    useEffect(() => {
      if (!assistantId) return;
      const raw = inboundAttached?.phone_number ?? inboundAttached?.number ?? null;
      const { phoneCode, phoneNumber } = parseFromNumber(raw);
      setValue('phoneCodeInbound', phoneCode, { shouldDirty: false });
      setValue('phoneNumberInbound', phoneNumber, { shouldDirty: false });
    }, [assistantId, inboundAttached, setValue]);

    const inboundOptions = useMemo(() => getPhoneNumberOptionsFromList(phoneListDataInbound), [phoneListDataInbound]);
    const outboundOptions = useMemo(() => getPhoneNumberOptionsFromList(phoneListData), [phoneListData]);
    const attachedInboundForAgent = useMemo(
      () => (inboundAttachList?.numbers ?? []).filter((n) => n.assistant_id === assistantId),
      [inboundAttachList?.numbers, assistantId]
    );

    const handleAttachInboundNumber = async () => {
      if (!assistantId || !selectedPhoneOptionInbound) return;
      const phoneNumberInboundVal = watch('phoneNumberInbound');
      if (!phoneNumberInboundVal?.trim()) {
        CtyShowToast(checkType.error, t('Please select a phone number.'));
        return;
      }
      const fullNumber = formatPhoneForApi(watch('phoneCodeInbound') ?? '', phoneNumberInboundVal);
      if (!fullNumber) {
        CtyShowToast(checkType.error, t('Please select a phone number.'));
        return;
      }
      try {
        await postInboundAttach({
          assistant_id: assistantId,
          phone_number: fullNumber,
          provider: selectedPhoneOptionInbound.provider,
        }).unwrap();
        CtyShowToast(checkType.success, t('Phone number attached successfully.'));
      } catch (err: unknown) {
        const detail = (err as { data?: { detail?: string }; status?: number })?.data?.detail ?? (err as Error)?.message ?? t('Failed to attach phone number.');
        CtyShowToast(checkType.error, String(detail));
      }
    };

    const [detachingNumber, setDetachingNumber] = useState<string | null>(null);

    const handleDetachInboundNumber = async (entry: { phone_number?: string; number?: string; provider?: string }) => {
      if (!assistantId) return;
      const num = entry.phone_number ?? entry.number ?? '';
      if (!num) return;
      setDetachingNumber(num);
      try {
        await deleteInboundAttach({
          assistant_id: assistantId,
          phone_number: num,
          provider: entry.provider ?? selectedPhoneOptionInbound?.provider,
        }).unwrap();
        CtyShowToast(checkType.success, t('Phone number detached.'));
      } catch (err: unknown) {
        const detail = (err as { data?: { detail?: string } })?.data?.detail ?? (err as Error)?.message ?? t('Failed to detach.');
        CtyShowToast(checkType.error, String(detail));
      } finally {
        setDetachingNumber(null);
      }
    };

    const toolsDirty = useMemo(
      () => !areBuiltInToolsEqual(builtInToolsState, savedBuiltInTools),
      [builtInToolsState, savedBuiltInTools]
    );
    const hasUnsavedChanges = isDirty || toolsDirty;

    const handleHangupToolChange = useCallback((enabled: boolean) => {
      setBuiltInToolsState((prev) => ({ ...prev, hangupEnabled: enabled }));
    }, []);

    const handleFormCaptureToolChange = useCallback((enabled: boolean) => {
      setBuiltInToolsState((prev) => ({ ...prev, formCaptureEnabled: enabled }));
    }, []);

    const handleWarmTransferToggle = useCallback((enabled: boolean) => {
      setBuiltInToolsState((prev) => ({
        ...prev,
        warmTransfer: { ...prev.warmTransfer, enabled },
      }));
    }, []);

    const handleWarmTransferConfigSave = useCallback((config: WarmTransferToolFormState) => {
      setBuiltInToolsState((prev) => ({ ...prev, warmTransfer: config }));
    }, []);

    const handleBusinessToolChange = useCallback((toolId: string, enabled: boolean) => {
      setBuiltInToolsState((prev) => {
        const businessTools = { ...prev.businessTools };
        if (enabled) {
          businessTools[toolId] = true;
        } else {
          delete businessTools[toolId];
        }
        return { ...prev, businessTools };
      });
    }, []);

    const onSave = async (values: EditAgentFormValues) => {
      if (!assistantId) return;
      try {
        const fromNumber =
          values.phoneCode && values.phoneNumber?.trim()
            ? formatPhoneForApi(values.phoneCode, values.phoneNumber.trim())
            : null;
        const resolvedFromNumber =
          fromNumber ?? (!values.from_number?.trim() ? null : normalizeFromNumberForApi(values.from_number)) ?? null;
        const patchValues: EditAgentFormValues = {
          ...values,
          pronunciation: values.pronunciation ?? getValues('pronunciation'),
        };
        const previousSnapshot = savedSnapshotRef.current;
        const body = buildVoiceAgentDeltaPatchBody(
          { ...patchValues, builtInTools: builtInToolsState },
          {
            ...(previousSnapshot?.values ?? {}),
            builtInTools: previousSnapshot?.builtInTools ?? parseVoiceAgentBuiltInToolsState(undefined),
          },
          {
            resolvedFromNumber,
            inboundFirstMessage: inboundEnabled,
            outboundFirstMessage: outboundEnabled,
          },
          {
            resolvedFromNumber: previousSnapshot?.resolvedFromNumber ?? resolvedFromNumber,
            inboundFirstMessage: previousSnapshot?.inboundEnabled ?? inboundEnabled,
            outboundFirstMessage: previousSnapshot?.outboundEnabled ?? outboundEnabled,
          }
        );

        if (Object.keys(body).length === 0) {
          if (!silentSave) CtyShowToast(checkType.success, t('No changes to save.'));
          resolveRef.current?.(true);
          resolveRef.current = null;
          return;
        }

        await updateAgent({ id: assistantId, body }).unwrap();
        savedSnapshotRef.current = {
          values: patchValues,
          inboundEnabled,
          outboundEnabled,
          resolvedFromNumber,
          builtInTools: builtInToolsState,
        };
        setSavedBuiltInTools(builtInToolsState);
        reset(patchValues, { keepDirty: false });
        if (!silentSave) CtyShowToast(checkType.success, t('Changes saved successfully.'));
        resolveRef.current?.(true);
        resolveRef.current = null;
      } catch (err) {
        const message =
          (err as { data?: { detail?: string }; message?: string })?.data?.detail ?? (err as Error)?.message ?? t('Failed to save changes.');
        CtyShowToast(checkType.error, message);
        resolveRef.current?.(false);
        resolveRef.current = null;
      }
    };

    const runSave = React.useCallback(() => {
      return new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
        handleSubmit(
          (values) => onSave(values),
          () => {
            resolveRef.current?.(false);
            resolveRef.current = null;
          }
        )();
      });
    }, [handleSubmit, onSave]);

    React.useEffect(() => {
      if (registerSaveHandler) {
        registerSaveHandler(runSave);
      }
    }, [registerSaveHandler, runSave]);

    const { data: advisorsData, isLoading: isAdvisorsLoading } = useGetAdvisorsQuery(false);
    const advisors = advisorsData?.data ?? [];
    const [advisorSearchTerm, setAdvisorSearchTerm] = useState('');
    const filteredAdvisors = useMemo(() => {
      const term = advisorSearchTerm.trim().toLowerCase();
      if (!term) return advisors;
      return advisors.filter(
        (a) =>
          (a.name ?? '').toLowerCase().includes(term) ||
          (a.description ?? '').toLowerCase().includes(term)
      );
    }, [advisors, advisorSearchTerm]);
    const { data: agentAdvisorTools, isLoading: isAgentAdvisorToolsLoading } = useGetAgentAdvisorToolsQuery(assistantId, { skip: !assistantId });
    const enabledAdvisorIds = useMemo(() => new Set(agentAdvisorTools?.advisor_ids ?? []), [agentAdvisorTools?.advisor_ids]);
    const [postAdvisorTool, { isLoading: isTogglingAdvisor }] = usePostAdvisorToolMutation();

    const isAdvisorEnabled = useCallback(
      (advisorId: string) => enabledAdvisorIds.has(advisorId),
      [enabledAdvisorIds]
    );

    const setAdvisorEnabled = useCallback(
      async (advisorId: string, enabled: boolean, name: string, description: string) => {
        if (!assistantId) return;
        try {
         const res = await postAdvisorTool({
            agent_id: assistantId,
            advisor_id: advisorId,
            name,
            description: description ?? '',
            enabled,
          }).unwrap();
          CtyShowToast(checkType.success, res.message);
        } catch (err) {
          const message = (err as { data?: { detail?: string }; message?: string })?.data?.detail ?? (err as Error)?.message ?? t('Failed to update advisor.');
          CtyShowToast(checkType.error, message);
        }
      },
      [assistantId, postAdvisorTool, t]
    );

    const [previewText, setPreviewText] = useState(preview_text);
    const [postVoicePreview] = usePostVoicePreviewMutation();
    const [previewingVoiceId, setPreviewingVoiceId] = useState<string | null>(null);
    const [voicePreviewLoadingId, setVoicePreviewLoadingId] = useState<string | null>(null);
    const previewAudioRef = useRef<HTMLAudioElement | null>(null);
    const previewMutationRef = useRef<{ abort: () => void } | null>(null);

    const handlePlayPreview = useCallback(
      async (voiceId: string, transcript?: string, provider?: string) => {
        const trimmedVoiceId = voiceId.trim();
        if (!trimmedVoiceId) {
          CtyShowToast(checkType.error, t('Please select a voice first.'));
          return;
        }

        const isActiveForVoice =
          previewingVoiceId === trimmedVoiceId || voicePreviewLoadingId === trimmedVoiceId;

        if (isActiveForVoice) {
          previewMutationRef.current?.abort();
          previewMutationRef.current = null;
          if (previewAudioRef.current) {
            previewAudioRef.current.pause();
            previewAudioRef.current = null;
          }
          setPreviewingVoiceId(null);
          setVoicePreviewLoadingId(null);
          return;
        }

        previewMutationRef.current?.abort();
        if (previewAudioRef.current) {
          previewAudioRef.current.pause();
          previewAudioRef.current = null;
        }
        setPreviewingVoiceId(null);
        setVoicePreviewLoadingId(null);

        const transcriptText =
          typeof transcript === 'string' && transcript.trim() !== ''
            ? transcript.trim()
            : t('Hello! This is a preview of my voice.');

        setVoicePreviewLoadingId(trimmedVoiceId);

        const promise = postVoicePreview({
          transcript: transcriptText,
          voiceId: trimmedVoiceId,
          provider,
        });
        previewMutationRef.current = promise;

        try {
          const blob = await promise.unwrap();
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          previewAudioRef.current = audio;

          audio.onended = () => {
            setPreviewingVoiceId(null);
            URL.revokeObjectURL(url);
            previewAudioRef.current = null;
          };

          await audio.play();
          setVoicePreviewLoadingId(null);
          setPreviewingVoiceId(trimmedVoiceId);
        } catch (error: unknown) {
          const aborted =
            (typeof error === 'object' &&
              error !== null &&
              'name' in error &&
              (error as { name: string }).name === 'AbortError') ||
            (typeof error === 'object' &&
              error !== null &&
              'message' in error &&
              String((error as { message: string }).message).toLowerCase().includes('abort'));
          if (aborted) {
            setPreviewingVoiceId(null);
            setVoicePreviewLoadingId(null);
            return;
          }

          const message =
            (error as { data?: { detail?: string }; error?: string })?.data?.detail ??
            (error as { error?: string })?.error ??
            (error as { message?: string })?.message ??
            t('Could not play voice preview.');
          CtyShowToast(checkType.error, String(message));
          setPreviewingVoiceId(null);
          setVoicePreviewLoadingId(null);
        } finally {
          if (previewMutationRef.current === promise) {
            previewMutationRef.current = null;
          }
        }
      },
      [postVoicePreview, previewingVoiceId, voicePreviewLoadingId, t]
    );

    const detail = agentDetail;
    if (isLoadingAgent) {
      return (
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" label={t('Loading Agent Details')} />
        </div>
      );
    }

    return (
      <div className="flex flex-col min-w-0 overflow-hidden rounded-lg border border-divider bg-default-50">
        <form className="flex flex-col min-h-0 min-w-0">
          {!hideSaveButton && (
            <div className="flex-shrink-0 px-6 py-4 border-b border-divider bg-default-100 flex justify-end gap-2">
              <CtyButton
                color="primary"
                isLoading={isSaving || isSavingInbound}
                isDisabled={!hasUnsavedChanges || !isValid}
                onPress={() => handleSubmit(onSave)()}
              >
                {t('Save changes')}
              </CtyButton>
            </div>
          )}
          <Tabs
            aria-label={t('Agent configuration')}
            variant="underlined"
            selectedKey={activeTab}
            onSelectionChange={(k) => setActiveTab(k as DetailTabKey)}
            classNames={{
              base: 'flex w-full min-w-0 max-w-full flex-shrink-0 overflow-hidden',
              tabList:
                'gap-3 w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden flex flex-nowrap px-4 sm:px-6 pt-2 border-b border-divider bg-default-100',
              cursor: 'bg-primary',
              tab: 'max-w-fit flex-none px-2 sm:px-3 h-10 whitespace-nowrap',
              tabContent: 'group-data-[selected=true]:text-primary',
              panel: 'px-0 py-0 min-w-0 max-h-[calc(100vh-370px)] overflow-y-auto',
            }}
          >
              {basicInfo && (
                <Tab key="Basic Info" title={t('Basic Info')} className="px-6 py-4">
                  <Card className="bg-default-50 border border-divider">
                    <CardBody className="gap-4">
                      <div>
                        <h3 className="text-base font-semibold text-foreground">{t('Basic Info')}</h3>
                        <p className="text-sm text-default-500 mt-0.5">
                          {t('Agent identity details for this voice worker.')}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 gap-4 max-w-2xl">
                        <Input
                          label={t('Agent name')}
                          value={basicInfo.agentName}
                          onValueChange={basicInfo.onAgentNameChange}
                          variant="bordered"
                          isRequired
                        />
                        <Input
                          label={t('Worker ID')}
                          value={basicInfo.workerId}
                          variant="bordered"
                          isReadOnly
                        />
                        <Input
                          label={t('Code')}
                          value={basicInfo.code ?? ''}
                          variant="bordered"
                          isReadOnly
                        />
                        <Textarea
                          label={t('Description')}
                          value={basicInfo.description}
                          onValueChange={basicInfo.onDescriptionChange}
                          variant="bordered"
                          minRows={4}
                          isRequired
                        />
                      </div>
                    </CardBody>
                  </Card>
                </Tab>
              )}
              <Tab key="Model" title={t('Model')} className="px-6 py-4">
                <div className="flex flex-col gap-4">
                  <Card className="bg-content1 border border-divider shadow-sm">
                    <CardBody className="gap-4 p-5">
                      <div>
                        <h3 className="text-base font-semibold text-foreground">{t('Model')}</h3>
                        <p className="text-sm text-default-500 mt-0.5">{t('Configure the behavior of the assistant.')}</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Controller
                        name="llm.provider"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label={t('Provider')}
                            selectedKeys={field.value ? [field.value] : []}
                            onSelectionChange={(keys) => {
                              const k = Array.from(keys)[0] as string | undefined;
                              field.onChange(k ?? '');
                              setValue('llm.model', '');
                            }}
                            variant="bordered"
                            isInvalid={!!errors.llm?.provider}
                            errorMessage={formErrorMessage(errors.llm?.provider)}
                          >
                            {(llmOpts?.providers ?? []).map((p) => (
                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                          </Select>
                        )}
                      />
                      <Controller
                        name="llm.model"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label={t('Model')}
                            selectedKeys={field.value ? [field.value] : []}
                            onSelectionChange={(keys) => {
                              const k = Array.from(keys)[0] as string | undefined;
                              field.onChange(k ?? '');
                            }}
                            variant="bordered"
                            isDisabled={!llmProvider}
                            isInvalid={!!errors.llm?.model}
                            errorMessage={formErrorMessage(errors.llm?.model)}
                          >
                            {llmModelsForProvider.map((m) => (
                              <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                            ))}
                          </Select>
                        )}
                      />
                    </div>
                    <Controller
                      name="llm.temperature"
                      control={control}
                      render={({ field }) => (
                        <Input
                          type="number"
                          label={t('Temperature')}
                          value={field.value == null ? '' : String(field.value)}
                          onValueChange={(v) => field.onChange(v === '' ? undefined : Number(v))}
                          variant="bordered"
                          description={t('Controls randomness of responses. Default is 0.7 when left empty.')}
                          isInvalid={!!errors.llm?.temperature}
                          errorMessage={formErrorMessage(errors.llm?.temperature)}
                        />
                      )}
                    />
                    </CardBody>
                  </Card>

                  <Card className="bg-content1 border border-divider shadow-sm">
                    <CardBody className="gap-4 p-5">
                      <div>
                        <h3 className="text-base font-semibold text-foreground">{t('Greeting')}</h3>
                        <p className="text-sm text-default-500 mt-0.5">
                          {t('Configure how the agent opens each call.')}
                        </p>
                      </div>
                      <Tabs
                        aria-label={t('Greeting direction')}
                        variant="underlined"
                        selectedKey={greetingTab}
                        onSelectionChange={(key) => setGreetingTab(key as 'inbound' | 'outbound')}
                        classNames={{
                          tabList: 'gap-4 border-b border-divider',
                          cursor: 'bg-primary',
                          tab: 'h-9 px-2',
                          tabContent: 'group-data-[selected=true]:text-primary text-sm',
                        }}
                      >
                        <Tab key="inbound" title={t('Inbound')}>
                          <div className="flex flex-col gap-4 pt-4">
                            <EditFirstMessageModeSelect
                              control={control}
                              name="first_message.mode"
                              label={t('First Message Mode')}
                              errorField={errors.first_message?.mode}
                            />
                            <EditFirstOpeningTextarea
                              control={control}
                              name="first_message.text"
                              label={t('Inbound Call Opening (When someone calls agent)')}
                              placeholder={t('Hello! Thanks for calling ...')}
                              firstMode={inboundFirstMode}
                              errorField={errors.first_message?.text}
                            />
                          </div>
                        </Tab>
                        <Tab key="outbound" title={t('Outbound')}>
                          <div className="flex flex-col gap-4 pt-4">
                            <EditFirstMessageModeSelect
                              control={control}
                              name="first_message_outbound.mode"
                              label={t('First Message Mode')}
                              errorField={errors.first_message_outbound?.mode}
                            />
                            <EditFirstOpeningTextarea
                              control={control}
                              name="first_message_outbound.text"
                              label={t('Outbound Call Opening (When agent calls someone)')}
                              placeholder={t('Hi! This is ...')}
                              firstMode={outboundFirstMode}
                              errorField={errors.first_message_outbound?.text}
                            />
                          </div>
                        </Tab>
                      </Tabs>
                    </CardBody>
                  </Card>

                  <Card className="bg-content1 border border-divider shadow-sm">
                    <CardBody className="gap-4 p-5">
                      <div>
                        <h3 className="text-base font-semibold text-foreground">{t('System Prompt')}</h3>
                        <p className="text-sm text-default-500 mt-0.5">
                          {t('Configure the assistant instructions for each call direction.')}
                        </p>
                      </div>
                      <SystemPromptInlineSection
                        control={control}
                        inboundFieldName="llm.system_prompt_template"
                        outboundFieldName="llm.system_prompt_template_outbound"
                        inboundError={errors.llm?.system_prompt_template}
                        outboundError={errors.llm?.system_prompt_template_outbound}
                        placeholder={t('System prompt...')}
                      />
                    </CardBody>
                  </Card>

                  <Card className="bg-content1 border border-divider shadow-sm">
                    <CardBody className="gap-4 p-5">
                      <VoiceAgentVariablesSection control={control} />
                    </CardBody>
                  </Card>
                </div>
              </Tab>

              <Tab key="Voice" title={t('Voice')} className="px-6 py-4">
                <Card className="bg-default-50 border border-divider">
                  <CardBody className="gap-4">
                    <div>
                      <h3 className="text-base font-semibold text-foreground">{t('Voice Configuration')}</h3>
                      <p className="text-sm text-default-500 mt-0.5">{t('View current voice and choose a new one.')}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-default-500 mb-2">{t('Current Voice')}</p>
                      <div
                        className={`grid grid-cols-1 gap-3 py-2 ${ttsProvider === 'azure' ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}
                      >
                        <div>
                          <span className="text-xs text-default-500">{t('Provider')}</span>
                          <p className="text-sm font-medium">{currentTtsProviderLabel}</p>
                        </div>
                        {ttsProvider !== 'azure' && (
                          <div>
                            <span className="text-xs text-default-500">{t('Model')}</span>
                            <p className="text-sm font-medium">{currentTtsModelLabel}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-xs text-default-500">{t('Voice')}</span>
                          <p className="text-sm font-medium">{currentTtsVoiceLabel}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-2">
                        <div>
                          <span className="text-xs text-default-500">{t('Emotion')}</span>
                          <p className="text-sm font-medium">
                            {formatTtsEmotionLabel(
                              ttsScalarString((detail?.tts as Record<string, unknown> | undefined)?.emotion)
                            )}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-default-500">{t('Volume')}</span>
                          <p className="text-sm font-medium">
                            {ttsNumericOrDefault(
                              (detail?.tts as Record<string, unknown> | undefined)?.volume,
                              TTS_VOLUME_DEFAULT
                            )}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-default-500">{t('Speed')}</span>
                          <p className="text-sm font-medium">
                            {ttsNumericOrDefault(
                              (detail?.tts as Record<string, unknown> | undefined)?.speed,
                              TTS_SPEED_DEFAULT
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">{t('Update Voice (root level)')}</p>
                      <p className="text-xs text-default-500 mb-3">
                        {ttsProvider === 'azure'
                          ? t('Provider and voice options are loaded from allowed-models (feature=tts).')
                          : t('Provider, voice and model options are loaded from allowed-models (feature=tts).')}
                      </p>
                      <div
                        className={`grid grid-cols-1 gap-4 ${ttsProvider === 'azure' ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}
                      >
                        <Controller
                          name="tts.provider"
                          control={control}
                          render={({ field }) => (
                            <Select
                              label={t('Voice Provider')}
                              placeholder={t('Select provider')}
                              selectedKeys={field.value ? [field.value] : []}
                              onSelectionChange={(keys) => {
                                const k = Array.from(keys)[0] as string | undefined;
                                field.onChange(k ?? '');
                                setValue('tts.voice_id', '');
                                setValue('tts.model_id', '');
                                setValue('tts.model', '');
                              }}
                              variant="bordered"
                              isInvalid={!!errors.tts?.provider}
                              errorMessage={formErrorMessage(errors.tts?.provider)}
                            >
                              {ttsProviderOptionsFromAllowed.map((o) => (
                                <SelectItem key={o.value} value={o.value} textValue={o.label}>{o.label}</SelectItem>
                              ))}
                            </Select>
                          )}
                        />
                        <Controller
                          name="tts.voice_id"
                          control={control}
                          render={({ field }) => (
                            <Select
                              label={t('Voice')}
                              placeholder={selectedTtsEntry ? t('Select voice') : t('Select provider first')}
                              selectedKeys={field.value ? [field.value] : []}
                              onSelectionChange={(keys) => {
                                const k = Array.from(keys)[0] as string | undefined;
                                field.onChange(k ?? '');
                              }}
                              isDisabled={!selectedTtsEntry || ttsVoiceOptionsFromAllowed.length === 0 || ttsProvider === 'dynamic'}
                              variant="bordered"
                              isInvalid={!!errors.tts?.voice_id}
                              errorMessage={formErrorMessage(errors.tts?.voice_id)}
                            >
                              {ttsVoiceOptionsFromAllowed.map((o) => (
                                <SelectItem key={o.value} value={o.value} textValue={o.label}>{o.label}</SelectItem>
                              ))}
                            </Select>
                          )}
                        />
                        {ttsProvider !== 'azure' && (
                          <Controller
                            name="tts.model_id"
                            control={control}
                            render={({ field }) => {
                              const modelValue =
                                (field.value && String(field.value).trim() !== ''
                                  ? field.value
                                  : watch('tts.model')) ?? '';
                              const modelStr = typeof modelValue === 'string' ? modelValue : String(modelValue ?? '');
                              return (
                                <Select
                                  label={t('Model')}
                                  placeholder={selectedTtsEntry ? t('Select model') : t('Select provider first')}
                                  selectedKeys={modelStr.trim() !== '' ? [modelStr] : []}
                                  onSelectionChange={(keys) => {
                                    const k = Array.from(keys)[0] as string | undefined;
                                    field.onChange(k ?? '');
                                    setValue('tts.model', k ?? '');
                                  }}
                                  isDisabled={!selectedTtsEntry || ttsModelOptionsFromAllowed.length === 0 || ttsProvider === 'dynamic'}
                                  variant="bordered"
                                  isInvalid={!!errors.tts?.model_id}
                                  errorMessage={formErrorMessage(errors.tts?.model_id)}
                                >
                                  {ttsModelOptionsFromAllowed.map((o) => (
                                    <SelectItem key={o.value} value={o.value} textValue={o.label}>{o.label}</SelectItem>
                                  ))}
                                </Select>
                              );
                            }}
                          />
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                        <Controller
                          name="tts.emotion"
                          control={control}
                          render={({ field }) => (
                            <Select
                              label={t('Emotion')}
                              placeholder={t('Select emotion')}
                              selectedKeys={field.value ? [field.value] : []}
                              onSelectionChange={(keys) => {
                                const k = Array.from(keys)[0] as string | undefined;
                                field.onChange(k ?? '');
                              }}
                              variant="bordered"
                            >
                              {TTS_EMOTION_OPTIONS.map(({ value, emoji }) => (
                                <SelectItem key={value} value={value} textValue={`${emoji} ${value}`}>
                                  <span className="flex items-center gap-2">
                                    <span aria-hidden="true">{emoji}</span>
                                    <span className="capitalize">{value}</span>
                                  </span>
                                </SelectItem>
                              ))}
                            </Select>
                          )}
                        />
                        <Controller
                          name="tts.volume"
                          control={control}
                          render={({ field }) => (
                            <div className="flex flex-col gap-2">
                              <span className="text-sm text-foreground">{t('Volume')}</span>
                              <Slider
                                aria-label={t('Volume')}
                                minValue={TTS_VOLUME_MIN}
                                maxValue={TTS_VOLUME_MAX}
                                step={TTS_SLIDER_STEP}
                                value={field.value ?? TTS_VOLUME_DEFAULT}
                                onChange={(value) => field.onChange(sliderChangeValue(value))}
                                showTooltip
                                size="md"
                                className="max-w-full"
                              />
                              <p className="text-xs text-default-500">
                                {(field.value ?? TTS_VOLUME_DEFAULT).toFixed(2)} ({TTS_VOLUME_MIN}–{TTS_VOLUME_MAX})
                              </p>
                            </div>
                          )}
                        />
                        <Controller
                          name="tts.speed"
                          control={control}
                          render={({ field }) => (
                            <div className="flex flex-col gap-2">
                              <span className="text-sm text-foreground">{t('Speed')}</span>
                              <Slider
                                aria-label={t('Speed')}
                                minValue={TTS_SPEED_MIN}
                                maxValue={TTS_SPEED_MAX}
                                step={TTS_SLIDER_STEP}
                                value={field.value ?? TTS_SPEED_DEFAULT}
                                onChange={(value) => field.onChange(sliderChangeValue(value))}
                                showTooltip
                                size="md"
                                className="max-w-full"
                              />
                              <p className="text-xs text-default-500">
                                {(field.value ?? TTS_SPEED_DEFAULT).toFixed(2)} ({TTS_SPEED_MIN}–{TTS_SPEED_MAX})
                              </p>
                            </div>
                          )}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 items-center">
                      <Textarea
                        placeholder={preview_text}
                        value={previewText}
                        onValueChange={setPreviewText}
                        variant="bordered"
                        minRows={2}
                      />
                      <CtyButton
                        size="sm"
                        variant="bordered"
                        color="primary"
                        isDisabled={!ttsVoiceIdValue || !previewText}
                        isLoading={voicePreviewLoadingId === ttsVoiceIdValue}
                        onPress={() => {
                          handlePlayPreview(ttsVoiceIdValue ?? '', previewText, ttsProvider ?? undefined);
                        }}
                        startContent={<Icon icon={previewingVoiceId === ttsVoiceIdValue ? "solar:pause-circle-bold" : "solar:play-circle-bold"} width={20} height={20} />}
                      >
                        {t('Preview voice')}
                      </CtyButton>
                    </div>
                  </CardBody>
                </Card>
              </Tab>

              <Tab key="Transcriber" title={t('Transcriber')} className="px-6 py-4">
                <Card className="bg-default-50 border border-divider">
                  <CardBody className="gap-4">
                    <div>
                      <h3 className="text-base font-semibold text-foreground">{t('Transcriber Configuration')}</h3>
                      <p className="text-sm text-default-500 mt-0.5">{t('View current STT config and select a new provider / model.')}</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-default-500">{t('Current Provider')}</span>
                        <p className="text-sm font-medium">{(detail?.stt as { provider?: string })?.provider ?? '—'}</p>
                      </div>
                      <div>
                        <span className="text-xs text-default-500">{t('Current Model')}</span>
                        <p className="text-sm font-medium">
                          {(detail?.stt as { live_options?: { model?: string }; model?: string })?.live_options?.model ?? (detail?.stt as { model?: string })?.model ?? '—'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">{t('Update Transcriber')}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Controller
                          name="stt.provider"
                          control={control}
                          render={({ field }) => (
                            <Select
                              label={t('STT Provider')}
                              selectedKeys={field.value ? [field.value] : []}
                              onSelectionChange={(keys) => {
                                const k = Array.from(keys)[0] as string | undefined;
                                field.onChange(k ?? '');
                                setValue('stt.live_options.model', '');
                                setValue('stt.model', '');
                              }}
                              variant="bordered"
                              isInvalid={!!errors.stt?.provider}
                              errorMessage={formErrorMessage(errors.stt?.provider)}
                            >
                              {(sttOpts?.providers ?? []).map((p) => (
                                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                              ))}
                            </Select>
                          )}
                        />
                        <Controller
                          name="stt.live_options.model"
                          control={control}
                          render={({ field }) => {
                            const modelValue =
                              (field.value && String(field.value).trim() !== ''
                                ? field.value
                                : watch('stt.model')) ?? '';
                            const modelStr = typeof modelValue === 'string' ? modelValue : String(modelValue ?? '');
                            return (
                            <Select
                              label={t('STT Model')}
                              selectedKeys={modelStr.trim() !== '' ? [modelStr] : []}
                              onSelectionChange={(keys) => {
                                const k = Array.from(keys)[0] as string | undefined;
                                field.onChange(k ?? '');
                                setValue('stt.model', k ?? '');
                              }}
                              variant="bordered"
                              isDisabled={!sttProvider}
                              isInvalid={!!errors.stt?.live_options?.model}
                              errorMessage={formErrorMessage(errors.stt?.live_options?.model)}
                            >
                              {sttModelsForProvider.map((m) => (
                                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                              ))}
                            </Select>
                            );
                          }}
                        />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Tab>

              <Tab key="Advance" title={t('Advance')} className="px-6 py-4">
                <div className="space-y-6">
                  <VoiceAgentFillerWordsSection control={control} setValue={setValue} />
                  <VoiceAgentIdleTimeoutSection control={control} setValue={setValue} />
                  <VoiceAgentVadSection control={control} />
                  <VoiceAgentKrispSection control={control} />
                  <VoiceAgentSummarizationSection
                    control={control}
                    setValue={setValue}
                    sttProviders={sttOpts?.providers ?? []}
                    sttModelsForProvider={summarizationModelsForProvider}
                  />
                </div>
              </Tab>

              <Tab key="Phone Number" title={t('Phone Number')} className="px-6 py-4">
                <div className="space-y-6">
                  <Card className="bg-default-50 border border-divider">
                    <CardBody className="gap-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-base font-semibold text-foreground">{t('Inbound Setting')}</h3>
                          <p className="text-sm text-default-500 mt-0.5">{t('Attach the phone number for inbound.')}</p>
                        </div>
                        <CtyButton size="sm" variant="flat" onPress={() => refetchInboundAttachList()} isDisabled={!assistantId} aria-label={t('Refresh')}>
                          {t('Refresh')}
                        </CtyButton>
                      </div>
                      {attachedInboundForAgent.length > 0 && (
                        <div className="rounded-lg border border-divider bg-default-100 p-4 space-y-2">
                          <p className="text-sm font-medium text-foreground">{t('In Use')}</p>
                          <ul className="space-y-2">
                            {attachedInboundForAgent.map((entry) => (
                              <li key={entry.phone_number ?? entry.number ?? ''} className="flex items-center justify-between gap-2">
                                <span className="text-sm text-success font-mono">
                                  {entry.phone_number ?? entry.number ?? '—'}
                                  {entry.provider && <span className="ml-2 text-default-500 text-xs">({entry.provider})</span>}
                                </span>
                                <CtyButton
                                  size="sm"
                                  color="danger"
                                  variant="light"
                                  onPress={() => handleDetachInboundNumber(entry)}
                                  isLoading={detachingNumber === (entry.phone_number ?? entry.number ?? '')}
                                  isDisabled={!!detachingNumber}
                                  aria-label={t('Detach')}
                                >
                                  {t('Detach')}
                                </CtyButton>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {attachedInboundForAgent.length === 0 && (
                        <p className="text-sm text-default-500">{t('No phone number attached to this agent yet.')}</p>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Controller
                          name="phoneCodeInbound"
                          control={control}
                          render={({ field }) => (
                            <Select
                              label={t('Provider')}
                              placeholder={t('Select provider')}
                              selectedKeys={field.value ? [field.value] : []}
                              onSelectionChange={(keys) => {
                                const k = Array.from(keys)[0] as string | undefined;
                                field.onChange(k ?? '');
                                setValue('phoneNumberInbound', '');
                              }}
                              variant="bordered"
                              classNames={{ trigger: 'min-w-[180px]' }}
                            >
                              {PHONE_PROVIDER_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </Select>
                          )}
                        />
                        <Controller
                          name="phoneNumberInbound"
                          control={control}
                          render={({ field }) => (
                            <Select
                              label={t('Inbound Phone Number')}
                              placeholder={
                                selectedPhoneOptionInbound
                                  ? (inboundOptions.length === 0 ? t('No numbers available') : t('Select inbound phone number'))
                                  : t('Select provider first')
                              }
                              selectedKeys={field.value?.trim() ? [field.value] : []}
                              onSelectionChange={(keys) => {
                                const k = Array.from(keys)[0] as string | undefined;
                                field.onChange(k ?? '');
                              }}
                              variant="bordered"
                              isDisabled={!selectedPhoneOptionInbound || inboundOptions.length === 0}
                              classNames={{ trigger: 'min-w-[180px]' }}
                            >
                              {inboundOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </Select>
                          )}
                        />
                      </div>
                      <div className="flex justify-end">
                        <CtyButton color="primary" onPress={handleAttachInboundNumber} isLoading={isSavingInbound} isDisabled={!selectedPhoneOptionInbound || !watch('phoneNumberInbound')?.trim()}>
                          {t('Attach Phone Number')}
                        </CtyButton>
                      </div>
                    </CardBody>
                  </Card>
                  <Card className="bg-default-50 border border-divider">
                    <CardBody className="gap-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-base font-semibold text-foreground">{t('Outbound Setting')}</h3>
                          <p className="text-sm text-default-500 mt-0.5">{t('Select a number to update outbound caller ID.')}</p>
                        </div>
                        {detail?.from_number && (
                          <div className="text-right">
                            <p className="text-[11px] text-default-500">{t('Current')}</p>
                            <Chip size="sm" variant="flat" color="success" className="font-mono">{detail.from_number}</Chip>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Controller
                          name="phoneCode"
                          control={control}
                          render={({ field }) => (
                            <Select
                              label={t('Provider')}
                              placeholder={t('Select provider')}
                              selectedKeys={field.value ? [field.value] : []}
                              onSelectionChange={(keys) => {
                                const k = Array.from(keys)[0] as string | undefined;
                                field.onChange(k ?? '');
                                setValue('phoneNumber', '');
                              }}
                              variant="bordered"
                              classNames={{ trigger: 'min-w-[180px]' }}
                            >
                              {PHONE_PROVIDER_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </Select>
                          )}
                        />
                        <Controller
                          name="phoneNumber"
                          control={control}
                          render={({ field }) => (
                            <Select
                              label={t('Outbound phone number')}
                              placeholder={
                                selectedPhoneOption
                                  ? (outboundOptions.length === 0 ? t('No numbers available') : t('Pick a number'))
                                  : t('Select provider first')
                              }
                              selectedKeys={field.value?.trim() ? [field.value] : []}
                              onSelectionChange={(keys) => {
                                const k = Array.from(keys)[0] as string | undefined;
                                field.onChange(k ?? '');
                              }}
                              variant="bordered"
                              isDisabled={!selectedPhoneOption || outboundOptions.length === 0}
                              classNames={{ trigger: 'min-w-[180px]' }}
                            >
                              {outboundOptions.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </Select>
                          )}
                        />
                      </div>
                      <p className="text-xs text-default-500">{t('Save changes to apply the outbound number.')}</p>
                    </CardBody>
                  </Card>
                </div>
              </Tab>

              <Tab key="Pronunciation" title={t('Pronunciation')} className="px-6 py-4">
                <Card className="bg-default-50 border border-divider">
                  <CardBody className="gap-4">
                    <Controller
                      name="pronunciation"
                      control={control}
                      defaultValue={{ enabled: false, dictionary_id: null }}
                      render={({ field }) => (
                        <PronunciationDictionarySection
                          selectedDictionaryIds={normalizePronunciationDictionaryIds(
                            field.value?.dictionary_id
                          )}
                          onUpdate={(config) => field.onChange(config)}
                        />
                      )}
                    />
                  </CardBody>
                </Card>
              </Tab>

              <Tab key="Tools" title={t('Tools')} className="px-6 py-4">
                <div className="flex flex-col gap-4">
                  <VoiceAgentBuiltInToolsSection
                    toolsState={builtInToolsState}
                    isUpdating={isSaving}
                    onHangupChange={handleHangupToolChange}
                    onFormCaptureChange={handleFormCaptureToolChange}
                    onWarmTransferToggle={handleWarmTransferToggle}
                    onWarmTransferConfigSave={handleWarmTransferConfigSave}
                    onBusinessToolChange={handleBusinessToolChange}
                  />

                  <Card className="bg-default-50 border border-divider">
                    <CardBody className="gap-4">
                      <div>
                        <h3 className="text-base font-semibold text-foreground">{t('Advisors')}</h3>
                        <p className="text-sm text-default-500 mt-0.5">{t('Enable or disable advisors for this voice agent.')}</p>
                      </div>
                      {isAdvisorsLoading || isAgentAdvisorToolsLoading ? (
                        <div className="flex items-center justify-center py-12 min-h-[200px]">
                          <Spinner size="lg" label={t('Loading advisors...')} />
                        </div>
                      ) : (
                        <>
                          <Input
                            placeholder={t('Search advisors...')}
                            value={advisorSearchTerm}
                            onValueChange={setAdvisorSearchTerm}
                            variant="bordered"
                            size="sm"
                            classNames={{ inputWrapper: 'max-w-full' }}
                            aria-label={t('Search advisors')}
                          />
                          <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
                            {filteredAdvisors.map((advisor) => (
                              <div key={advisor.advisor_id} className="flex items-start justify-between gap-4 rounded-lg border border-divider p-4">
                                <div className="flex flex-col min-w-0">
                                  <h3 className="text-base font-medium text-foreground">{advisor.name}</h3>
                                  {advisor.description && (
                                    <p className="text-sm text-default-500 mt-0.5">{advisor.description}</p>
                                  )}
                                </div>
                                <Switch
                                  isSelected={isAdvisorEnabled(advisor.advisor_id)}
                                  onValueChange={(enabled) => setAdvisorEnabled(advisor.advisor_id, enabled, advisor.name, advisor.description ?? '')}
                                  isDisabled={isTogglingAdvisor}
                                  aria-label={t('Enable {{name}}', { name: advisor.name })}
                                />
                              </div>
                            ))}
                            {filteredAdvisors.length === 0 && (
                              <p className="text-sm text-default-500">
                                {advisorSearchTerm.trim() ? t('No matching advisors.') : t('No advisors available.')}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </CardBody>
                  </Card>
                </div>
              </Tab>
            </Tabs>
        </form>
      </div>
    );
  }

/** Props for the create-voice-agent form (no agent name; name is in parent). */
export interface VoiceAgentCreateFormProps {
  control: Control<VoiceAgentFormValues>;
  setValue: UseFormSetValue<VoiceAgentFormValues>;
  getValues: UseFormGetValues<VoiceAgentFormValues>;
  watch: UseFormWatch<VoiceAgentFormValues>;
  allowedModelsLoading: boolean;
  templates: TemplateEntry[];
  selectedTemplateId: string;
  onTemplateChange: (templateId: string) => void;
  showAddTtsConfig: boolean;
  setShowAddTtsConfig: (v: boolean) => void;
  addTtsProvider: string;
  setAddTtsProvider: (v: string) => void;
  addTtsLanguage: string;
  setAddTtsLanguage: (v: string) => void;
  addTtsVoiceId: string;
  setAddTtsVoiceId: (v: string) => void;
  llmOpts: { providers: Array<{ value: string; label: string }> };
  sttOpts: { providers: Array<{ value: string; label: string }> };
  ttsOpts: Array<{ value: string; label: string }>;
  llmModelsForProvider: Array<{ value: string; label: string }>;
  sttModelsForProvider: Array<{ value: string; label: string }>;
  ttsConfigs: Record<string, TtsLanguageConfig> | undefined;
  allowedTtsLangOptions: Array<{ value: string; label: string }>;
  ttsAllVoiceOptions: Array<{ provider: string; voice_id?: string; model_id?: string }>;
  /** Phone list for the selected provider (create flow); same query as parent when `phoneCode` is set. */
  voiceAgentPhoneListData: PhoneNumberListResponse | undefined;
  voiceAgentPhoneListFetching: boolean;
}

/** Create voice agent form content (same as create agent modal except no agent name). */
function VoiceAgentCreateForm(props: VoiceAgentCreateFormProps) {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    getValues,
    allowedModelsLoading,
    templates,
    selectedTemplateId,
    onTemplateChange,
    showAddTtsConfig,
    setShowAddTtsConfig,
    addTtsProvider,
    setAddTtsProvider,
    addTtsLanguage,
    setAddTtsLanguage,
    addTtsVoiceId,
    setAddTtsVoiceId,
    llmOpts,
    sttOpts,
    ttsOpts,
    llmModelsForProvider,
    sttModelsForProvider,
    ttsConfigs,
    allowedTtsLangOptions,
    ttsAllVoiceOptions,
    voiceAgentPhoneListData,
    voiceAgentPhoneListFetching,
  } = props;
  const llmProvider = props.watch('llm.provider');
  const sttProvider = props.watch('stt.provider');
  const createPhoneCode = props.watch('phoneCode');
  const selectedCreatePhoneProvider = useMemo(
    () => PHONE_PROVIDER_OPTIONS.find((o) => o.value === createPhoneCode),
    [createPhoneCode]
  );
  const createPhoneNumberOptions = useMemo(
    () => getPhoneNumberOptionsFromList(voiceAgentPhoneListData),
    [voiceAgentPhoneListData]
  );
  let createPhoneNumberPlaceholder = t('Pick a number');
  if (!selectedCreatePhoneProvider) {
    createPhoneNumberPlaceholder = t('Select provider first');
  } else if (voiceAgentPhoneListFetching) {
    createPhoneNumberPlaceholder = t('Loading numbers...');
  } else if (createPhoneNumberOptions.length === 0) {
    createPhoneNumberPlaceholder = t('No numbers available');
  }

  if (allowedModelsLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" label={t('Loading models...')} />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="flex flex-col gap-4 pb-2">
        <Select
          label={t('Template')}
          placeholder={t('Select a template')}
          selectedKeys={selectedTemplateId ? [selectedTemplateId] : []}
          onSelectionChange={(keys) => {
            const k = Array.from(keys)[0] as string | undefined;
            onTemplateChange(k ?? '');
          }}
          variant="bordered"
          description={t('Optional: prefill the form with a template configuration.')}
        >
          {templates.map((template) => (
            <SelectItem key={template.id} value={template.id} description={template.description ?? ''} textValue={template.label}>
              {template.label}
            </SelectItem>
          ))}
        </Select>
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">{t('Agent Phone Number (optional)')}</p>
          <div className="flex gap-2">
            <Controller
              name="phoneCode"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder={t('Select provider')}
                  selectedKeys={field.value ? [field.value] : []}
                  onSelectionChange={(keys) => {
                    const k = Array.from(keys)[0] as string | undefined;
                    field.onChange(k ?? '');
                    setValue('from_number', '');
                  }}
                  onBlur={field.onBlur}
                  variant="bordered"
                  classNames={{ trigger: 'min-w-[180px]' }}
                  aria-label={t('Phone provider and country')}
                >
                  {PHONE_PROVIDER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              name="from_number"
              control={control}
              render={({ field }) => (
                <Select
                  placeholder={createPhoneNumberPlaceholder}
                  selectedKeys={field.value?.trim() ? [field.value] : []}
                  onSelectionChange={(keys) => {
                    const k = Array.from(keys)[0];
                    field.onChange(typeof k === 'string' ? k : '');
                  }}
                  onBlur={field.onBlur}
                  variant="bordered"
                  classNames={{ trigger: 'min-w-[180px] flex-1' }}
                  aria-label={t('Phone number')}
                  isDisabled={
                    !selectedCreatePhoneProvider ||
                    voiceAgentPhoneListFetching ||
                    createPhoneNumberOptions.length === 0
                  }
                >
                  {createPhoneNumberOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} textValue={opt.label}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </Select>
              )}
            />
          </div>
          <p className="text-xs text-default-500">{t("If you don't select a number, the agent will use a default number.")}</p>
        </div>
        <Controller
          name="first_message.text"
          control={control}
          render={({ field }) => (
            <Input label={t('First Message (Inbound)')} placeholder={t('Hello! How can I help you?')} value={field.value ?? ''} onValueChange={field.onChange} onBlur={field.onBlur} variant="bordered" />
          )}
        />
        <Controller
          name="first_message_outbound.text"
          control={control}
          render={({ field }) => (
            <Input label={t('First Message (Outbound)')} placeholder={t('Hi! This is Circuitry AI, calling regarding...')} value={field.value ?? ''} onValueChange={field.onChange} onBlur={field.onBlur} variant="bordered" />
          )}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            name="llm.provider"
            control={control}
            render={({ field }) => (
              <Select
                label={t('LLM Provider')}
                placeholder={t('Select provider')}
                selectedKeys={field.value ? [String(field.value)] : []}
                onSelectionChange={(keys) => {
                  const k = Array.from(keys)[0] as string | undefined;
                  field.onChange(k ?? '');
                  setValue('llm.model', '');
                }}
                onBlur={field.onBlur}
                variant="bordered"
                aria-label={t('LLM Provider')}
              >
                {(llmOpts?.providers ?? []).map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </Select>
            )}
          />
          <Controller
            name="llm.model"
            control={control}
            render={({ field }) => (
              <Select
                label={t('LLM Model')}
                placeholder={t('Select model')}
                selectedKeys={field.value ? [field.value] : []}
                onSelectionChange={(keys) => {
                  const k = Array.from(keys)[0] as string | undefined;
                  field.onChange(k ?? '');
                }}
                onBlur={field.onBlur}
                variant="bordered"
                isDisabled={!llmProvider}
                aria-label={t('LLM Model')}
              >
                {llmModelsForProvider.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </Select>
            )}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            name="stt.provider"
            control={control}
            render={({ field }) => (
              <Select
                label={t('STT Provider')}
                placeholder={t('Select provider')}
                selectedKeys={field.value ? [field.value] : []}
                onSelectionChange={(keys) => {
                  const k = Array.from(keys)[0] as string | undefined;
                  field.onChange(k ?? '');
                  setValue('stt.live_options.model', '');
                  setValue('stt.model', '');
                }}
                onBlur={field.onBlur}
                variant="bordered"
                aria-label={t('STT Provider')}
              >
                {(sttOpts?.providers ?? []).map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </Select>
            )}
          />
          <Controller
            name="stt.live_options.model"
            control={control}
            render={({ field }) => {
              const modelValue =
                (field.value && String(field.value).trim() !== ''
                  ? field.value
                  : props.watch('stt.model')) ?? '';
              const modelStr = typeof modelValue === 'string' ? modelValue : String(modelValue ?? '');
              return (
              <Select
                label={t('STT Model')}
                placeholder={t('Select model')}
                selectedKeys={modelStr.trim() !== '' ? [modelStr] : []}
                onSelectionChange={(keys) => {
                  const k = Array.from(keys)[0] as string | undefined;
                  field.onChange(k ?? '');
                  setValue('stt.model', k ?? '');
                }}
                onBlur={field.onBlur}
                variant="bordered"
                isDisabled={!sttProvider}
                aria-label={t('STT Model')}
              >
                {sttModelsForProvider.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </Select>
              );
            }}
          />
        </div>
        <Controller
          name="tts.provider"
          control={control}
          render={({ field }) => (
            <Select
              label={t('TTS Provider')}
              placeholder={t('Select TTS provider')}
              selectedKeys={field.value ? [field.value] : []}
              onSelectionChange={(keys) => {
                const k = Array.from(keys)[0] as string | undefined;
                field.onChange(k ?? '');
                const firstVoice = ttsAllVoiceOptions.find((p) => p.provider === (k ?? ''));
                if (firstVoice) setValue('tts.voice_id', firstVoice.voice_id ?? '');
              }}
              onBlur={field.onBlur}
              variant="bordered"
              aria-label={t('TTS Provider')}
            >
              {ttsOpts.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </Select>
          )}
        />
        {(() => {
          const hasConfigs = ttsConfigs && Object.keys(ttsConfigs).length > 0;
          const showTtsConfigsSection = hasConfigs || showAddTtsConfig;
          return showTtsConfigsSection ? (
            <div className="space-y-3 rounded-lg border border-default-200 p-3">
              <p className="text-small text-default-500">{t('TTS languages (multilingual)')}</p>
              {hasConfigs && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(ttsConfigs).map(([lang, cfg]) => (
                    <Chip
                      key={lang}
                      variant="flat"
                      onClose={() => {
                        const next = { ...ttsConfigs };
                        delete next[lang];
                        setValue('tts.configs', Object.keys(next).length ? next : undefined);
                        if (Object.keys(next).length === 0) {
                          setValue('tts.provider', ttsOpts[0]?.value ?? '');
                          setValue('tts.voice_id', ttsAllVoiceOptions[0]?.voice_id ?? '');
                        }
                      }}
                      classNames={{ base: 'gap-1', content: 'flex flex-col items-start gap-0' }}
                    >
                      <span className="flex items-center gap-1">
                        <span className="font-medium">{lang}</span>
                        <span className="font-semibold text-primary">{cfg.voice_id}</span>
                      </span>
                      <span className="text-default-500 text-tiny">{cfg.provider}</span>
                    </Chip>
                  ))}
                </div>
              )}
              {showAddTtsConfig ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <Select
                    label={t('Provider')}
                    placeholder={t('Select provider')}
                    selectedKeys={addTtsProvider ? [addTtsProvider] : []}
                    onSelectionChange={(keys) => setAddTtsProvider(Array.from(keys)[0] as string ?? '')}
                    variant="bordered"
                  >
                    {ttsOpts.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </Select>
                  <Select
                    label={t('Language')}
                    placeholder={t('Select language')}
                    selectedKeys={addTtsLanguage ? [addTtsLanguage] : []}
                    onSelectionChange={(keys) => setAddTtsLanguage(Array.from(keys)[0] as string ?? '')}
                    variant="bordered"
                  >
                    {allowedTtsLangOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </Select>
                  <Select
                    label={t('Voice')}
                    placeholder={t('Select voice')}
                    selectedKeys={addTtsVoiceId ? [addTtsVoiceId] : []}
                    onSelectionChange={(keys) => setAddTtsVoiceId(Array.from(keys)[0] as string ?? '')}
                    variant="bordered"
                  >
                    {ttsAllVoiceOptions.map((p) => (
                      <SelectItem key={p.voice_id ?? p.model_id ?? ''} value={p.voice_id ?? ''}>
                        {p.voice_id ?? t('Default')}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              ) : null}
              <div className="flex gap-2">
                {showAddTtsConfig ? (
                  <>
                    <CtyButton
                      size="sm"
                      color="primary"
                      onPress={() => {
                        if (!addTtsProvider || !addTtsLanguage || !addTtsVoiceId) {
                          CtyShowToast(checkType.error, t('Please select provider, language and voice.'));
                          return;
                        }
                        const preset = ttsAllVoiceOptions.find((p) => (p.voice_id ?? '') === addTtsVoiceId);
                        const newConfig: TtsLanguageConfig = { provider: addTtsProvider, voice_id: addTtsVoiceId, model_id: preset?.model_id };
                        const prev = getValues('tts.configs') ?? {};
                        const next = { ...prev, [addTtsLanguage]: newConfig };
                        setValue('tts.configs', next);
                        setValue('tts.provider', 'dynamic');
                        if (!getValues('tts.default_language')) setValue('tts.default_language', addTtsLanguage);
                        setAddTtsProvider('');
                        setAddTtsLanguage('');
                        setAddTtsVoiceId('');
                        setShowAddTtsConfig(false);
                      }}
                    >
                      {t('Add')}
                    </CtyButton>
                    <CtyButton size="sm" variant="flat" onPress={() => { setShowAddTtsConfig(false); setAddTtsProvider(''); setAddTtsLanguage(''); setAddTtsVoiceId(''); }}>
                      {t('Cancel')}
                    </CtyButton>
                  </>
                ) : (
                  <CtyButton size="sm" variant="flat" onPress={() => setShowAddTtsConfig(true)}>
                    {t('Add language')}
                  </CtyButton>
                )}
              </div>
            </div>
          ) : null;
        })()}
        <style>{markdownPreviewStyles}</style>
        <style>{promptPreviewStyles}</style>
        <Controller
          name="llm.system_prompt_template"
          control={control}
          render={({ field }) => (
            <div className="flex flex-col gap-2 w-full">
              <div className="flex flex-col gap-3 w-full md:flex-row md:gap-4">
                <div className="w-full md:w-1/2 flex flex-col gap-1">
                  <label className="text-sm font-medium text-foreground">{t('System Prompt (Inbound)')}</label>
                  <Textarea placeholder={t('Inbound prompt...')} value={field.value ?? ''} onValueChange={field.onChange} onBlur={field.onBlur} variant="bordered" classNames={{ inputWrapper: 'voice-agent-prompt-textarea' }} />
                </div>
                <div className="w-full md:w-1/2 flex flex-col gap-1">
                  <span className="text-sm font-semibold text-default-600">{t('Preview')}</span>
                  <div className="voice-agent-prompt-preview rounded-medium border border-divider bg-default-50 p-3 overflow-y-auto" style={{ height: PROMPT_PREVIEW_HEIGHT_PX, minHeight: PROMPT_PREVIEW_HEIGHT_PX, maxHeight: PROMPT_PREVIEW_HEIGHT_PX, fontSize: '14px', lineHeight: '1.5' }}>
                    <ReactMarkdown>{processMarkdownText(field.value ?? '')}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          )}
        />
        <Controller
          name="llm.system_prompt_template_outbound"
          control={control}
          render={({ field }) => (
            <div className="flex flex-col gap-2 w-full">
              <div className="flex flex-col gap-3 w-full md:flex-row md:gap-4">
                <div className="w-full md:w-1/2 flex flex-col gap-1">
                  <label className="text-sm font-medium text-foreground">{t('System Prompt (Outbound)')}</label>
                  <Textarea placeholder={t('Outbound prompt...')} value={field.value ?? ''} onValueChange={field.onChange} onBlur={field.onBlur} variant="bordered" classNames={{ inputWrapper: 'voice-agent-prompt-textarea' }} />
                </div>
                <div className="w-full md:w-1/2 flex flex-col gap-1">
                  <span className="text-sm font-semibold text-default-600">{t('Preview')}</span>
                  <div className="voice-agent-prompt-preview rounded-medium border border-divider bg-default-50 p-3 overflow-y-auto" style={{ height: PROMPT_PREVIEW_HEIGHT_PX, minHeight: PROMPT_PREVIEW_HEIGHT_PX, maxHeight: PROMPT_PREVIEW_HEIGHT_PX, fontSize: '14px', lineHeight: '1.5' }}>
                    <ReactMarkdown>{processMarkdownText(field.value ?? '')}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          )}
        />
      </div>
    </div>
  );
}

export interface VoiceAgentContentProps {
  /** 'create' for /agents/create, 'update' for /agents/update */
  mode: 'create' | 'update';
  /** When set (from aiworkers_attrs voice_agent_id), show update form with tabs. When null, show create form. */
  voiceAgentId: string | null;
  /** Props for the create form (used in create mode or update mode when no voiceAgentId). */
  createFormProps: VoiceAgentCreateFormProps;
  /** Basic info fields shown in the first tab when editing an existing voice agent. */
  basicInfo?: VoiceAgentBasicInfoProps;
  /** When provided (update with tabs), the tabs form hides its Save button and registers a handler. Parent calls it first, then update agent API. */
  onRegisterVoiceSaveHandler?: (runSave: () => Promise<boolean>) => void;
}

/**
 * Renders voice agent section under Agent Type.
 * - Create mode: always show create form.
 * - Update mode + voiceAgentId: show tabs form (update).
 * - Update mode + no voiceAgentId: show create form (no data for tabs).
 */
export const VoiceAgentContent = ({
  mode,
  voiceAgentId,
  createFormProps,
  basicInfo,
  onRegisterVoiceSaveHandler,
}: VoiceAgentContentProps) => {
  const showUpdateTabs = mode === 'update' && voiceAgentId != null && voiceAgentId !== '';
  if (showUpdateTabs) {
    return (
      <VoiceAgentDetailFormTabs
        assistantId={voiceAgentId}
        basicInfo={basicInfo}
        hideSaveButton={!!onRegisterVoiceSaveHandler}
        registerSaveHandler={onRegisterVoiceSaveHandler}
        silentSave={!!onRegisterVoiceSaveHandler}
      />
    );
  }
  return <VoiceAgentCreateForm {...createFormProps} />;
}

export default VoiceAgentContent;
